/**
 * THE HERO-PLUGIN CONTRACT (ADR-0099 / ADR-0100 / ADR-0102 / ADR-0110) — the web-only abstractions the core
 * render layer calls. The core never branches on a hero id: it resolves a {@link HeroPlugin} from the
 * registry (`heroPluginFor` in `./plugins`, which fills any omitted method over a base) and calls
 * `renderFigure` / `renderAvatar` / `renderScene` / `renderItemIcon` over OPAQUE hero data. Every
 * hero-specific thing (its body art, its looks, its world, its theme) lives in `content/heroes/<hero>/` and
 * is delivered through this contract. A hero with no plugin resolves to the NEUTRAL plugin so a brand-new
 * data-only hero renders (an orb) instead of crashing.
 *
 * ADR-0110 made heroes RASTER skins (full-look-swap): a plugin's render methods return an `<svg>` wrapping
 * `<image>` tiles, equipping a look swaps the whole figure (no SVG-fragment compositing). The legacy
 * mix-and-match surface (per-item `<symbol>` fragments, `CosmeticLayout` crops, the global symbol sheet) is
 * gone — a hero declares ~one look-place (`<hero>_look`) plus an optional scene place.
 *
 * ADR-0102 makes the surface OPEN + ADDITIVE: `renderAvatar`/`renderScene`/`renderItemIcon` are OPTIONAL
 * (so a minimal hero authors only `renderFigure`), every render arg carries a render-instance `uid` (for
 * namespacing internal SVG ids so the same look drawn several times on one page never collides), `renderScene`
 * draws the hero's equipped BACKGROUND (the chosen scene cosmetic) — the SAME backdrop on every surface, so
 * the kid `/hero`, the wardrobe and the platform matrix never disagree about what the hero stands on — and a
 * hero may declare an `idleClassName` + react to a {@link HeroMood}. None of it is required, so every existing
 * hero stays byte-identical until it opts in.
 *
 * This is a dependency-free LEAF (types only, no import of the registry) so every per-hero `skin.tsx` can
 * implement it without a cycle through the aggregating registry.
 */
import type { ReactElement } from 'react';
import {
  defineHeroThemeContract,
  validateHeroThemeContrast,
  type HeroThemeTokens,
} from '@kidshome/ui';

/**
 * A child's equipped look (ADR-0099 / ADR-0110) — a HERO-AGNOSTIC map from the hero's wear-PLACE id to the
 * equipped item slug (or null). The core never assumes a fixed set of places: each hero's plugin reads ITS
 * OWN place ids out of this map (royal reads `royal_look` + `royal_scene`; dash reads `dash_look`). Since
 * ADR-0110 a hero declares ~one look-place — equipping a look swaps the whole figure (full-look-swap).
 */
export type EquippedLook = Readonly<Record<string, string | null>>;

/** Read the item equipped in a place (null if nothing / the place isn't worn). */
export function lookItem(look: EquippedLook, place: string): string | null {
  return look[place] ?? null;
}

/**
 * The hero's calm EXPRESSION hint (ADR-0102), set by the surface from live events — `cheer` while a fresh
 * effort is landing, `idle` otherwise. Optional everywhere: a plugin may react to it (a hand-up / smile) or
 * ignore it. Kept a small named union (not a free string) so a plugin can exhaustively switch on it.
 */
export type HeroMood = 'idle' | 'cheer';

/** A render-instance token (from the React wrapper's `useId`) used to namespace a fragment's internal ids. */
export interface RenderInstance {
  readonly uid: string;
}

/** Args for the full-body figure — the kid `/hero`, the wardrobe, the platform matrix. */
export interface HeroFigureArgs extends RenderInstance {
  readonly stageIndex: number;
  readonly stageCount?: number;
  readonly look: EquippedLook;
  readonly className?: string;
  readonly mood?: HeroMood;
}

/** Args for the circular bust (the avatar wraps the returned inner SVG in the masked circle). */
export interface HeroAvatarArgs extends RenderInstance {
  readonly stageIndex: number;
  readonly look: EquippedLook;
}

/** Context for a backdrop draw — the render-instance uid + the current form. */
export interface HeroSceneContext extends RenderInstance {
  readonly stageIndex?: number;
}

/** Args for a catalog/wardrobe TILE — the same worn art cropped to its place. */
export interface HeroItemIconArgs extends RenderInstance {
  readonly slug: string;
  readonly place: string;
  readonly className?: string;
}

/**
 * The bespoke RENDER plugin one hero ships, authored in `content/heroes/<hero>/skin.tsx`. Everything is in the
 * hero's OWN coordinate space; the core attaches no meaning to any of it. Only {@link HeroPlugin.renderFigure}
 * is required — `heroPluginFor` fills any omitted method over a base (avatar = the figure, scene = none,
 * itemIcon = the neutral tinted tile), so a minimal hero still renders everywhere.
 */
export interface HeroPlugin {
  readonly archetype: string;
  /** Optional CSS class for the hero's IDLE motion (each surface falls back to its own default breathe). */
  readonly idleClassName?: string;
  /** The full-body figure at a form, wearing the look — the kid `/hero`, the wardrobe, the platform matrix. */
  renderFigure(p: HeroFigureArgs): ReactElement;
  /** The inner SVG for the circular bust (optional → defaults to the figure). */
  renderAvatar?(p: HeroAvatarArgs): ReactElement;
  /** The hero's equipped BACKGROUND scene — the SAME backdrop on every surface (optional → none = bare sky). */
  renderScene?(look: EquippedLook, ctx: HeroSceneContext): ReactElement | null;
  /** The catalog/wardrobe TILE for one item (optional → the neutral tinted tile). */
  renderItemIcon?(p: HeroItemIconArgs): ReactElement;
}

/**
 * The plugin the CORE consumes — every render method present. `heroPluginFor` returns this, filling any
 * method a hero omitted over a base, so a consumer never null-checks a render method.
 */
export type ResolvedHeroPlugin = HeroPlugin & {
  renderAvatar: NonNullable<HeroPlugin['renderAvatar']>;
  renderScene: NonNullable<HeroPlugin['renderScene']>;
  renderItemIcon: NonNullable<HeroPlugin['renderItemIcon']>;
};

// The hero THEME contract lives in the design system (the kid shell honours exactly this override set).
export { defineHeroThemeContract, validateHeroThemeContrast, type HeroThemeTokens };
